import DescriptionIcon from "@mui/icons-material/Description";
import { Card } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import useCreateTransfer from "@/hooks/requests/t2Pay/use-create-payment";
import useGetFinancialTransaction from "@/hooks/requests/t2Pay/use-get-financial-transaction";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import usePopOver from "@/hooks/use-popover";
import { AccountBalance, PaymentPayload } from "@/interfaces/talk2pay.iterfaces";
import { T2PayHomeMessages } from "@/messages/platforms/talk2PayHome/t2pay-home.messages";
import { PlatformsName } from "@/types/platform.types";

import { ExportStatementPopover } from "./ExportStatementPopover";
import PaymentDialog from "./PaymentDialog";
import { StatementTable } from "./StatementTable";

type Props = {
  messages: T2PayHomeMessages;
  balanceData: AccountBalance;
  statementFilterStartDate: Date;
  statementFilterEndDate: Date;
  onStatementFilterChange: (startDate: Date, endDate: Date) => void;
};
export default function StatementTab({
  messages,
  balanceData,
  statementFilterStartDate,
  statementFilterEndDate,
  onStatementFilterChange,
}: Props) {
  const { isVisible, handleOpen, handleClose } = useDialog();
  const { getPlatformByIdentifier } = usePlatformId();
  const platform = getPlatformByIdentifier(PlatformsName.ASAAS);
  const platformId = platform?.id;
  const { showToast } = useToast();
  const { mutate } = useMutate();
  const [startDate, setStartDate] = useState<Dayjs | null>(() => dayjs(statementFilterStartDate));
  const [endDate, setEndDate] = useState<Dayjs | null>(() => dayjs(statementFilterEndDate));
  const { create, isLoading: isLoadingTransfer } = useCreateTransfer();

  const {
    data: financialTransaction,
    isUpdating,
    updateFinancialTransaction,
    error,
  } = useGetFinancialTransaction({
    startDate: statementFilterStartDate,
    endDate: statementFilterEndDate,
  });
  const { openPopover, anchorEl, closePopover, isOpen } = usePopOver<null>();

  const handleUpdateFinancialTransaction = useCallback(
    (newStartDate?: Date, newEndDate?: Date) => {
      if (newStartDate && newEndDate) {
        onStatementFilterChange(newStartDate, newEndDate);
        updateFinancialTransaction(newStartDate, newEndDate);
      }
    },
    [onStatementFilterChange, updateFinancialTransaction]
  );

  const lastEmptyShownRef = useRef(false);
  useEffect(() => {
    const isEmpty =
      !isUpdating &&
      !!financialTransaction &&
      financialTransaction.transactions.length === 0;
    if (isEmpty && !lastEmptyShownRef.current) {
      showToast({
        duration: 3000,
        text: messages.noTransactionsInPeriod,
        type: "warning",
      });
      lastEmptyShownRef.current = true;
    }
    if (!isEmpty) {
      lastEmptyShownRef.current = false;
    }
  }, [financialTransaction, isUpdating, messages.noTransactionsInPeriod, showToast]);

  const onSuccess = () => {
    mutate(SWR_KEYS.getAccountBalance);
    mutate(SWR_KEYS.getFinancialTransaction);
    handleClose();
  };

  const onError = () => handleClose();

  const handleSubmit = (_formData: PaymentPayload) => {
    if (!platformId) return showToast({ text: messages.generalErrorMessage, type: "error" });

    const successMessage = messages.createTransferSuccess;
    const errorMessage = messages.createTransferError;

    const formData: PaymentPayload = { ..._formData, platformId };
    create({ formData, successMessage, errorMessage, onSuccess, onError });
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" alignItems={"center"} justifyContent={"space-between"}>
          <MDTypography variant="h5">{messages.statementTabTitle}</MDTypography>
        </MDBox>
        <MDBox mt={3} display={"flex"} justifyContent="flex-end" gap={2}>
          <DatePicker
            label={"Início"}
            value={startDate}
            onChange={(value) => setStartDate(value)}
            slotProps={{
              textField: {
                variant: "outlined",
                InputLabelProps: { shrink: true },
              },
            }}
          />
          <DatePicker
            label={"Fim"}
            value={endDate}
            onChange={(value) => setEndDate(value)}
            slotProps={{
              textField: {
                variant: "outlined",
                InputLabelProps: { shrink: true },
              },
            }}
          />
          <MDButton
            variant="gradient"
            isLoading={isUpdating}
            color="dark"
            sx={{ textWrap: "nowrap" }}
            onClick={() => handleUpdateFinancialTransaction(startDate?.toDate(), endDate?.toDate())}
          >
            {messages.filterButton}
          </MDButton>
          <MDButton
            variant="gradient"
            color="dark"
            startIcon={<DescriptionIcon />}
            sx={{ textWrap: "nowrap" }}
            onClick={(e) => {
              e.stopPropagation();
              openPopover(e, null);
            }}
          >
            {messages.exportStatementButtonLabel}
          </MDButton>

          <ExportStatementPopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            financialTransactionStartDate={statementFilterStartDate}
            financialTransactionEndDate={statementFilterEndDate}
          />
        </MDBox>

        <MDBox mt={3}>
          <StatementTable messages={messages} financialTransaction={financialTransaction} />
        </MDBox>

        <MDBox mt={3} display="flex" justifyContent={"flex-end"}>
          <MDButton
            variant="gradient"
            color="dark"
            sx={{ textWrap: "nowrap" }}
            onClick={() => handleOpen()}
          >
            {messages.withdrawButtonLabel}
          </MDButton>
        </MDBox>

        <PaymentDialog
          isOpen={isVisible}
          onClose={handleClose}
          messages={messages}
          onSubmit={handleSubmit}
          isLoadingTransfer={isLoadingTransfer}
          balanceData={balanceData}
        />

        {error && (
          <AlertBlock
            title={messages.errorTitle}
            description={messages.errorDescription}
            alt={messages.errorAlt}
          />
        )}
      </MDBox>
    </Card>
  );
}
